import type { FC } from 'react';
import { useIntl } from 'react-intl';

import type { OrderDetailsOrder } from '@/types/orderDetailsOrder';

import { Box } from '@/components/Box';
import { Col } from '@/components/Col';
import { DateOrdinal } from '@/components/DateOrdinal';
import { Flex } from '@/components/Flex';
import { Grid } from '@/components/Grid';
import { ORDER_STATUS_MESSAGE_MAPPING } from '@/components/MyOrders/OrderStatusStepper';
import { Typography } from '@/components/Typography';
import type { OrderAppOrderStatus } from '@/graphql/orderApp/api';
import { CONTENTSQUARE_MASKED } from '@/lib/forms/const';
import * as FM from '@/messages/forms';
import * as OM from '@/messages/order';
import * as PM from '@/messages/payment';
import * as S from '@/views/OrderDetailsView/OrderDetailsView.styles';
import { getShippingDisplayName } from '@/views/OrderReturnView/orderReturnUtils';

export type ReturnOrderSummaryProps = {
  isHomeDelivery: boolean;
  isMobileView: boolean;
  order: OrderDetailsOrder;
  trackingNumber: string | undefined;
};

export const ReturnOrderSummary: FC<ReturnOrderSummaryProps> = ({
  isHomeDelivery,
  isMobileView,
  order,
  trackingNumber,
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <S.OrderDetailHeader>
        <Typography
          data-test-id='OrderReturnViewTitle'
          fontWeight='bold'
          marginBottom={2}
          scale='h4'
          textAlign={isMobileView ? 'center' : 'left'}
          textTransform='uppercase'
        >
          {formatMessage(PM.paymentMessages.selectItemsToReturn)}
        </Typography>
      </S.OrderDetailHeader>

      <Box backgroundColor='lightGrey' pb={2} pl={2} pr={2}>
        <Flex flexWrap='wrap' justifyContent='space-between' width='100%'>
          <Box maxWidth={isMobileView ? 200 : 213} pt={2}>
            <Grid>
              <Col xs={12}>
                <Typography
                  color='secondaryGrey'
                  data-test-id='OrderReturnViewOrderCreationDate'
                >
                  {OM.OrderCreationDate}
                </Typography>
              </Col>
              <Col xs={12}>
                <Typography
                  data-test-id='OrderReturnViewOrderCreationDateValue'
                  fontWeight='bold'
                >
                  <DateOrdinal date={order.created} />
                </Typography>
              </Col>
            </Grid>
          </Box>
          <Box maxWidth={isMobileView ? 200 : 213} pt={2}>
            <Grid>
              <Col xs={12}>
                <Typography
                  color='secondaryGrey'
                  data-test-id='OrderReturnViewOrderNumberLabel'
                >
                  {OM.OrderNumber}
                </Typography>
              </Col>
              <Col xs={12}>
                <Typography
                  data-test-id='OrderReturnViewOrderNumberValue'
                  fontWeight='bold'
                >
                  {order.businessId}
                </Typography>
              </Col>
            </Grid>
          </Box>
          <Box maxWidth={isMobileView ? 200 : 213} pt={2}>
            <Grid>
              <Col xs={12}>
                <Typography
                  color='secondaryGrey'
                  data-test-id='OrderReturnViewOrderStatusLabel'
                >
                  {OM.OrderStatus}
                </Typography>
              </Col>
              <Col xs={12}>
                {order.orderStatus ? (
                  <Typography
                    color='primary'
                    data-test-id='OrderReturnViewOrderStatus'
                    fontWeight='bold'
                    textTransform='uppercase'
                  >
                    {
                      ORDER_STATUS_MESSAGE_MAPPING[
                        order.orderStatus as OrderAppOrderStatus
                      ]
                    }
                  </Typography>
                ) : null}
              </Col>
            </Grid>
          </Box>
          {isHomeDelivery ? (
            <Box maxWidth={isMobileView ? 200 : 213} pt={2}>
              <Grid>
                <Col xs={12}>
                  <Typography
                    color='secondaryGrey'
                    data-test-id='OrderReturnViewTrackingNumberLabel'
                  >
                    {OM.TrackingNumber}
                  </Typography>
                </Col>
                <Col xs={12}>
                  <Typography
                    data-test-id='OrderReturnViewTrackingNumberValue'
                    fontWeight='bold'
                  >
                    {trackingNumber ?? '-'}
                  </Typography>
                </Col>
              </Grid>
            </Box>
          ) : null}
        </Flex>
        <Flex
          flexWrap='wrap'
          gridGap={3}
          justifyContent='flex-start'
          marginTop={2}
          width='100%'
        >
          <Box
            maxWidth={isMobileView ? 200 : 213}
            pt={isMobileView ? 2 : undefined}
          >
            <Grid>
              <Col xs={12}>
                <Typography
                  color='secondaryGrey'
                  data-test-id='OrderReturnViewCustomerNameLabel'
                >
                  {formatMessage(PM.paymentMessages.returnFlowCustomerName)}
                </Typography>
              </Col>
              <Col xs={12}>
                <Typography
                  className={CONTENTSQUARE_MASKED}
                  data-test-id='OrderReturnViewCustomerNameValue'
                  fontWeight='bold'
                >
                  {getShippingDisplayName(order)}
                </Typography>
              </Col>
            </Grid>
          </Box>
          <Box
            maxWidth={isMobileView ? 200 : 213}
            pt={isMobileView ? 2 : undefined}
          >
            <Grid>
              <Col xs={12}>
                <Typography
                  color='secondaryGrey'
                  data-test-id='OrderReturnViewEmailLabel'
                >
                  {formatMessage(FM.formMessages.email)}
                </Typography>
              </Col>
              <Col xs={12}>
                <Typography
                  className={CONTENTSQUARE_MASKED}
                  data-test-id='OrderReturnViewEmailValue'
                  fontWeight='bold'
                >
                  {order.userEmail}
                </Typography>
              </Col>
            </Grid>
          </Box>
        </Flex>
      </Box>
    </>
  );
};
